import { useSyncExternalStore } from 'react';
import { create } from 'zustand';

/*
 * Installable app (SPEC §57): service-worker registration, "new version" notice, install
 * prompt, connectivity and the app badge. The service worker only exists in production builds
 * (`pwa/vite-plugin.ts`); in development and tests these functions do nothing.
 */

/** Chrome's install prompt event (not in the DOM typings yet). */
export interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface PwaState {
  /** A new version is installed and waits for a reload. */
  updateReady: boolean;
  /** The browser offers installation (Chrome, Edge, Android). */
  installPrompt: BeforeInstallPromptEvent | null;
  installed: boolean;
}

export const usePwaStore = create<PwaState>(() => ({ updateReady: false, installPrompt: null, installed: false }));

let waiting: ServiceWorker | null = null;
let applyingUpdate = false;

function watchUpdates(registration: ServiceWorkerRegistration): void {
  const offer = (worker: ServiceWorker | null) => {
    // With a controller, an installed worker is an update (the first install is not).
    if (worker && navigator.serviceWorker.controller) {
      waiting = worker;
      usePwaStore.setState({ updateReady: true });
    }
  };
  if (registration.waiting) offer(registration.waiting);
  registration.addEventListener('updatefound', () => {
    const worker = registration.installing;
    worker?.addEventListener('statechange', () => {
      if (worker.state === 'installed') offer(worker);
    });
  });
}

/** Call once at start-up. */
export function initPwa(): void {
  if (typeof window === 'undefined') return;
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    usePwaStore.setState({ installPrompt: event as BeforeInstallPromptEvent });
  });
  window.addEventListener('appinstalled', () => usePwaStore.setState({ installPrompt: null, installed: true }));

  if (!import.meta.env.PROD || !('serviceWorker' in navigator)) return;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    // Only after the user asked for the new version; the very first activation must not reload.
    if (applyingUpdate) window.location.reload();
  });
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js')
      .then((registration) => {
        watchUpdates(registration);
        // Long-open tabs (a lobby kiosk, a dashboard) look for new versions every hour.
        setInterval(() => void registration.update().catch(() => undefined), 60 * 60 * 1000);
      })
      .catch(() => undefined);
  });
}

/** Activates the waiting version; the page reloads once it has taken over. */
export function applyUpdate(): void {
  if (!waiting) return;
  applyingUpdate = true;
  waiting.postMessage({ type: 'SKIP_WAITING' });
}

export async function promptInstall(): Promise<void> {
  const prompt = usePwaStore.getState().installPrompt;
  if (!prompt) return;
  await prompt.prompt();
  const { outcome } = await prompt.userChoice;
  usePwaStore.setState({ installPrompt: null, installed: outcome === 'accepted' });
}

/** Running as an installed app (home-screen icon) rather than in a browser tab. */
export function isStandalone(): boolean {
  return window.matchMedia?.('(display-mode: standalone)').matches === true || (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

/** iPhone / iPad Safari, which installs through Share → Add to Home Screen instead of a prompt. */
export function isIosSafari(): boolean {
  const ua = navigator.userAgent;
  return /iP(hone|ad|od)/.test(ua) && /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
}

function subscribeOnline(onChange: () => void): () => void {
  window.addEventListener('online', onChange);
  window.addEventListener('offline', onChange);
  return () => {
    window.removeEventListener('online', onChange);
    window.removeEventListener('offline', onChange);
  };
}

/** Whether the browser reports a network connection (it may still be unable to reach the server). */
export function useOnlineStatus(): boolean {
  return useSyncExternalStore(subscribeOnline, () => navigator.onLine, () => true);
}

/** Unread count on the installed app's icon, where the platform supports it. */
export function setAppBadge(count: number): void {
  const nav = navigator as Navigator & { setAppBadge?: (n?: number) => Promise<void>; clearAppBadge?: () => Promise<void> };
  if (count > 0) void nav.setAppBadge?.(count).catch(() => undefined);
  else void nav.clearAppBadge?.().catch(() => undefined);
}
